import React, { useState, useCallback, useEffect } from "react";
import FlowNodeEditorShell from "../FlowBuilder/FlowNodeEditorShell";
import { useFlowNodeEdit } from "../../hooks/useFlowNodeEdit";
import { getNodeEditorMeta } from "../../utils/flowNodeEditorMeta";
import { Handle, Position } from "reactflow";
import { UserPlus, Pencil, Check, RotateCw } from "lucide-react";
import { getAllAgents } from "../../apis/agent";
import NodeActionButtons from "./NodeActionButtons";
import NodeEditorTabs from "./NodeEditorTabs";

// ─── Collapsed View ───────────────────────────────────
const CollapsedView = ({ data, onEdit }) => {
  const agentNames = data.agentNames || [];
  const agentIds = data.agentIds || [];
  const n = agentIds.length;
  const method = data.allocationMethod === "roundRobin" ? "Round Robin" : "Round Robin";
  const summary =
    n === 0
      ? data.agentId != null && data.agentName
        ? data.agentName
        : "No agents selected"
      : n === 1
        ? `${agentNames[0] || "1 agent"}`
        : n <= 3
          ? agentNames.slice(0, n).join(", ")
          : `${agentNames.slice(0, 2).join(", ")} +${n - 2}`;
  const showCount = n > 0 || (data.agentId != null && data.agentName);
  const displayN = n > 0 ? n : data.agentId != null ? 1 : 0;

  return (
    <div
      className="relative rounded-xl overflow-hidden"
      style={{
        backgroundColor: "#E0E7FF",
        padding: "10px",
        minWidth: "260px",
        maxWidth: "320px",
      }}
    >
      <button
        onClick={(e) => {
          e.stopPropagation();
          onEdit();
        }}
        className="absolute top-2 right-2 z-10 p-1.5 bg-white/90 hover:bg-white rounded-full shadow-md transition-all hover:shadow-lg"
        title="Edit assign agent"
      >
        <Pencil size={12} className="text-indigo-600" />
      </button>

      <div className="bg-white rounded-lg shadow-sm p-3">
        <div className="flex items-center gap-2 mb-2">
          <div className="p-1.5 bg-indigo-100 rounded-lg">
            <UserPlus size={14} className="text-indigo-600" />
          </div>
          <span className="text-[12px] font-bold text-indigo-900">Assign Agent</span>
        </div>
        <div className="flex items-center gap-1.5 mb-1">
          <RotateCw size={10} className="text-indigo-500 flex-shrink-0" />
          <span className="text-[10px] font-semibold text-indigo-700">{method}</span>
          {showCount && displayN > 0 && (
            <span className="text-[9px] text-indigo-600 bg-indigo-50 px-1.5 py-0.5 rounded">
              {displayN} agent{displayN !== 1 ? "s" : ""}
            </span>
          )}
        </div>
        <div className="text-[11px] text-indigo-700 truncate" title={summary}>
          {summary}
        </div>
      </div>
    </div>
  );
};

// ─── Expanded Editor ──────────────────────────────────
const ExpandedEditor = ({ data, onSave, onCancel }) => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState(() => {
    if (data.agentIds && Array.isArray(data.agentIds) && data.agentIds.length > 0) {
      return new Set(data.agentIds.map(Number));
    }
    if (data.agentId != null) {
      return new Set([Number(data.agentId)]);
    }
    return new Set();
  });

  useEffect(() => {
    let cancelled = false;
    getAllAgents()
      .then((res) => {
        if (!cancelled && res?.data?.agents) setAgents(res.data.agents || []);
      })
      .catch(() => {
        if (!cancelled) setAgents([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const toggleAgent = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => {
    setSelectedIds(new Set(agents.map((a) => a.id)));
  };

  const clearAll = () => {
    setSelectedIds(new Set());
  };

  const handleSave = () => {
    const ids = agents.filter((a) => selectedIds.has(a.id)).map((a) => a.id);
    const names = agents.filter((a) => selectedIds.has(a.id)).map((a) => a.name || "");
    onSave({
      agentIds: ids,
      agentNames: names,
      allocationMethod: "roundRobin",
    });
  };

  return (
    <div
      className="nowheel"
      style={{
        backgroundColor: "#E0E7FF",
        borderRadius: "12px",
        padding: "14px",
        minWidth: "300px",
        maxWidth: "380px",
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="p-1.5 bg-indigo-200 rounded-lg">
            <UserPlus size={14} className="text-indigo-700" />
          </div>
          <span className="text-[13px] font-bold text-indigo-900">Assign Agent</span>
        </div>
        <div className="flex gap-1.5">
          <button
            onClick={handleSave}
            className="px-3 py-1.5 text-[11px] font-bold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center gap-1 shadow-sm"
          >
            <Check size={12} /> Save
          </button>
          <button
            onClick={onCancel}
            className="px-3 py-1.5 text-[11px] font-bold bg-white text-gray-600 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
          >
            Cancel
          </button>
        </div>
      </div>

      <NodeEditorTabs nodeType="assignAgent">
      <div className="bg-white rounded-lg shadow-sm p-3 space-y-3">
        <div className="flex items-center justify-between">
          <label className="block text-[10px] font-bold text-gray-600 uppercase tracking-wider">
            Allocation method
          </label>
          <span className="flex items-center gap-1 text-[10px] font-semibold text-indigo-700">
            <RotateCw size={10} /> Round Robin
          </span>
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <label className="block text-[10px] font-bold text-gray-600 uppercase tracking-wider">
              Agents
            </label>
            <div className="flex gap-1.5">
              <button
                type="button"
                onClick={selectAll}
                className="text-[9px] font-bold text-indigo-600 hover:text-indigo-700"
              >
                Select all
              </button>
              <span className="text-gray-300">|</span>
              <button
                type="button"
                onClick={clearAll}
                className="text-[9px] font-bold text-gray-500 hover:text-gray-700"
              >
                Clear
              </button>
            </div>
          </div>
          {loading ? (
            <p className="text-[11px] text-gray-500 py-2">Loading agents…</p>
          ) : agents.length === 0 ? (
            <p className="text-[11px] text-gray-500 py-2">No agents available</p>
          ) : (
            <div className="max-h-[220px] overflow-y-auto space-y-1.5 pr-1">
              {agents.map((agent) => (
                <label
                  key={agent.id}
                  className="flex items-center gap-2 py-1.5 px-2 rounded-lg hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={selectedIds.has(agent.id)}
                    onChange={() => toggleAgent(agent.id)}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-300"
                  />
                  <span className="text-[11px] font-medium text-gray-800">{agent.name}</span>
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      </NodeEditorTabs>
      <div className="flex gap-2 mt-3">
        <button
          onClick={handleSave}
          className="flex-1 py-2 text-[12px] font-bold bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors flex items-center justify-center gap-1.5 shadow-sm"
        >
          <Check size={14} /> Save
        </button>
        <button
          onClick={onCancel}
          className="flex-1 py-2 text-[12px] font-bold bg-white text-gray-600 rounded-lg hover:bg-gray-100 transition-colors border border-gray-200"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

// ─── Main AssignAgentNode ──────────────────────────────
const AssignAgentNode = ({ id, data }) => {
  const { isEditing, openEditor, closeEditor } = useFlowNodeEdit(id, data);
  const editorMeta = getNodeEditorMeta("assignAgent");

  const handleSave = useCallback(
    (formData) => {
      if (data.onNodeDataChange) {
        data.onNodeDataChange(id, { ...data, ...formData });
      }
      closeEditor();
    },
    [id, data, closeEditor]);

  return (
    <div
      className={`relative transition-all shadow-md`}
      style={{ borderRadius: "12px" }}
    >
      <NodeActionButtons id={id} data={data} />
      <Handle
        type="target"
        position={Position.Left}
        className="!w-3.5 !h-3.5 !bg-indigo-500 !border-2 !border-white !shadow-sm"
        style={{ left: -7 }}
      />

      <CollapsedView data={data} onEdit={openEditor} />
      <FlowNodeEditorShell
        open={isEditing}
        onClose={closeEditor}
        title={data.label || editorMeta.label}
        subtitle={editorMeta.label}
        icon={editorMeta.icon}
        accent={editorMeta.accent}
      >
        <ExpandedEditor
          data={data}
          onSave={handleSave}
          onCancel={() => closeEditor()}
         presentation="fullscreen" onCancel={closeEditor} />
      </FlowNodeEditorShell>

      <Handle
        type="source"
        position={Position.Right}
        className="!w-3.5 !h-3.5 !bg-indigo-500 !border-2 !border-white !shadow-sm"
        style={{ right: -7 }}
      />
    </div>
  );
};

export default AssignAgentNode;
